import React, { useState } from 'react';
import api from '../utils/api';
import ConfirmModal from './ConfirmModal';

/**
 * New starter forms the client has emailed to employees.
 *
 * There is no approve step. The client authorised the starter when they set the
 * salary and start date and pressed Send; a completed form goes straight into
 * their payroll changes, where they can remove it before submitting like any
 * other change. So this is a status display, not a queue of decisions — which is
 * why it is a strip and a list rather than a table full of buttons.
 *
 * Both halves take their data from the dashboard so the page fetches once.
 */

const LIST_ANCHOR = 'starter-forms';

function statusOf(invite) {
  if (invite.status === 'queued') return 'queued';
  // Submitted under the old two-step flow and never added. Without this it fell
  // through to 'sent' and the row claimed they hadn't replied — while the
  // Resend button they were offered refused, saying they had.
  if (invite.status === 'completed') return 'stranded';
  if (invite.expired) return 'expired';
  return 'sent';
}

const PILLS = {
  sent: { label: 'Awaiting reply', cls: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' },
  expired: { label: 'Link expired', cls: 'bg-rose-100 text-rose-800 ring-1 ring-rose-200' },
  queued: { label: 'In your changes', cls: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200' },
  stranded: { label: 'Sent — being added', cls: 'bg-sky-100 text-sky-800 ring-1 ring-sky-200' },
};

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function sentAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * A single line near the top of the dashboard, in the same shape as the pension
 * alert, saying what is still outstanding. Nothing to show once every form is
 * back — a form that has arrived is a payroll change now, and the changes card
 * above already counts it.
 */
export function StarterFormsStrip({ invites = [] }) {
  const waiting = invites.filter(i => statusOf(i) === 'sent');
  const expired = invites.filter(i => statusOf(i) === 'expired');
  if (waiting.length === 0 && expired.length === 0) return null;

  const parts = [];
  if (waiting.length) parts.push(`${waiting.length} new starter form${waiting.length === 1 ? '' : 's'} out with employees`);
  if (expired.length) parts.push(`${expired.length} link${expired.length === 1 ? '' : 's'} expired`);

  return (
    <div className="rounded-lg border p-4 mb-6 bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800 flex items-center justify-between gap-4">
      <div className="flex items-center space-x-3">
        <svg className="w-5 h-5 flex-shrink-0 text-brand-600 dark:text-brand-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-brand-800 dark:text-brand-200">{parts.join(' · ')}</p>
          <p className="text-xs text-brand-700 dark:text-brand-300 mt-0.5">
            {expired.length > 0
              ? "An expired link can't be used — send a new one."
              : "They'll appear in your payroll changes as soon as they're filled in."}
          </p>
        </div>
      </div>
      <a href={`#${LIST_ANCHOR}`}
        className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 whitespace-nowrap">
        View
      </a>
    </div>
  );
}

/**
 * The full list, below the employee table where a table has room. Resend and
 * cancel apply only to a form nobody has filled in yet — once it's back, it is
 * an ordinary payroll change and it's that change they'd remove.
 */
export function StarterFormsList({ invites = [], onToast, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  if (invites.length === 0) return null;

  const act = async (invite, action) => {
    setBusyId(invite.id);
    try {
      if (action === 'resend') {
        const res = await api.post(`/starter-invites/${invite.id}/resend`);
        onToast?.({ message: res.data.message, type: 'success' });
      } else if (action === 'cancel') {
        const res = await api.delete(`/starter-invites/${invite.id}`);
        onToast?.({ message: res.data.message, type: 'success' });
      }
      onChanged?.();
    } catch (err) {
      onToast?.({ message: err.response?.data?.error || 'That did not work. Please try again.', type: 'error' });
    } finally {
      setBusyId(null);
      setConfirmCancel(null);
    }
  };

  return (
    <div id={LIST_ANCHOR} className="mt-8 scroll-mt-6">
      <div className="mb-3">
        <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-brand-700">New starters</p>
        <h2 className="font-heading text-[20px] text-ink dark:text-gray-100 mt-1 leading-tight">Forms sent to employees</h2>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1">
          They fill in their own details, and land in your payroll changes when they're done.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">New starter</th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Starts</th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {invites.map(invite => {
                const state = statusOf(invite);
                const pill = PILLS[state];
                const busy = busyId === invite.id;
                return (
                  <tr key={invite.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {invite.first_name} {invite.last_name}
                      </span>
                      <br />
                      <span className="text-[12px] text-gray-500">{invite.email}</span>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-600 dark:text-gray-400 tabular">{shortDate(invite.start_date)}</td>
                    <td className="hidden sm:table-cell px-4 py-3 text-sm text-gray-600 dark:text-gray-400 tabular">{sentAt(invite.created_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center rounded font-semibold tracking-wide px-2 py-0.5 text-[13px] ${pill.cls}`}>
                        {pill.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {state === 'sent' && (
                        <>
                          <button type="button" disabled={busy} onClick={() => act(invite, 'resend')}
                            className="px-2.5 py-1.5 text-[12.5px] font-medium text-brand-700 ring-1 ring-brand-200 bg-white dark:bg-gray-800 rounded-md hover:bg-brand-50 disabled:opacity-50">
                            Resend
                          </button>{' '}
                          <button type="button" disabled={busy} onClick={() => setConfirmCancel(invite)}
                            className="px-2.5 py-1.5 text-[12.5px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50">
                            Cancel
                          </button>
                        </>
                      )}
                      {state === 'expired' && (
                        <button type="button" disabled={busy} onClick={() => act(invite, 'resend')}
                          className="px-2.5 py-1.5 text-[12.5px] font-medium text-brand-700 ring-1 ring-brand-200 bg-white dark:bg-gray-800 rounded-md hover:bg-brand-50 disabled:opacity-50">
                          Send a new link
                        </button>
                      )}
                      {state === 'queued' && (
                        <span className="text-[12.5px] text-gray-500">Nothing to do</span>
                      )}
                      {state === 'stranded' && (
                        <span className="text-[12.5px] text-gray-500">Nothing to do</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {confirmCancel && (
        <ConfirmModal
          title={`Cancel the form for ${confirmCancel.first_name}?`}
          message={`The link stops working straight away. ${confirmCancel.first_name} won't be able to send their details, and you'd need to send a new form.`}
          confirmText="Cancel the form"
          onConfirm={() => act(confirmCancel, 'cancel')}
          onCancel={() => setConfirmCancel(null)}
        />
      )}
    </div>
  );
}
